import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { AppointmentListItem } from '@/hooks/useAppointments';
import { fetchAppointmentInvoiceLinks } from '@/hooks/useAppointmentInvoicing';
import {
  getCalendarAppointmentStatus,
  getCalendarBillingPresentation,
  getCalendarJournalAttention,
  pickCanonicalAppointmentJournal,
  type CalendarBillingPresentation,
  type CalendarJournalEntryMeta,
  type CalendarJournalMeta,
} from '@/utils/appointmentCalendarMeta';
import type { CalendarLifecycleStatus } from '@/utils/appointmentCalendarStyle';

export interface CalendarAppointmentMeta {
  appointmentStatus: CalendarLifecycleStatus;
  billing: CalendarBillingPresentation;
  journal: CalendarJournalMeta;
  invoiceId?: string | null;
}

export function useCalendarAppointmentMeta(
  clinicId: string | null | undefined,
  appointments: AppointmentListItem[],
) {
  const ids = appointments.map((a) => a.id).sort();
  const idsKey = ids.join(',');

  const journalsQ = useQuery({
    queryKey: ['calendar', 'journals', clinicId, idsKey],
    enabled: Boolean(clinicId && ids.length > 0),
    staleTime: 15_000,
    queryFn: async (): Promise<CalendarJournalEntryMeta[]> => {
      if (!clinicId || ids.length === 0) return [];
      const { data, error } = await supabase
        .from('journal_entries')
        .select(
          'id, appointment_id, content_md, created_at, updated_at, finalized_at, is_primary, discarded_at',
        )
        .eq('clinic_id', clinicId)
        .in('appointment_id', ids)
        .is('discarded_at', null);
      if (error) throw error;
      return ((data ?? []) as CalendarJournalEntryMeta[]).filter((row) =>
        Boolean(row.appointment_id),
      );
    },
  });

  const invoicesQ = useQuery({
    queryKey: ['calendar', 'invoices', clinicId, idsKey],
    enabled: Boolean(clinicId && ids.length > 0),
    staleTime: 15_000,
    queryFn: async () => {
      if (!clinicId || ids.length === 0) return [];
      const map = await fetchAppointmentInvoiceLinks(ids);
      return Array.from(map.values());
    },
  });

  const journalByAppointment = new Map<string, CalendarJournalEntryMeta>();
  const journalRows = journalsQ.data ?? [];
  const appointmentIds = new Set(appointments.map((appointment) => appointment.id));
  for (const appointmentId of appointmentIds) {
    const canonical = pickCanonicalAppointmentJournal(journalRows, appointmentId);
    if (canonical) journalByAppointment.set(appointmentId, canonical);
  }

  const invoiceByAppointment = new Map(
    (invoicesQ.data ?? []).map((row) => [row.appointment_id, row] as const),
  );

  const byId = new Map<string, CalendarAppointmentMeta>();
  for (const appointment of appointments) {
    const invoice = invoiceByAppointment.get(appointment.id);
    const billing = getCalendarBillingPresentation({
      payment_method: appointment.payment_method,
      payment_status: appointment.payment_status,
      visit_status: appointment.visit_status,
      invoiceStatus: invoice?.invoice_status,
      invoicePaymentMethod: invoice?.payment_method,
      status: appointment.status,
      starts_at: appointment.starts_at,
      ends_at: appointment.ends_at,
    });
    byId.set(appointment.id, {
      appointmentStatus: getCalendarAppointmentStatus(appointment, billing),
      billing,
      journal: getCalendarJournalAttention({
        appointment,
        journal: journalByAppointment.get(appointment.id) ?? null,
      }),
      invoiceId: invoice?.invoice_id ?? null,
    });
  }

  return byId;
}
